import loadAirmarAnalyticsSettings from "@aus/Handlers/LoadAirmarAnalyticsSettings";
import { AusState } from "@aus/Store/AusState";
import { getHeadTrackingScript, getNoscriptTrackingScript } from "@aus/Utilities/tracking";
import { getCookie, removeCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useGetSetting from "@insite/client-framework/Common/Hooks/useGetSetting";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const COOKIE_CONSENT = "acceptCookies";
const COOKIE_ANALYTICS = "cookieConsent_analytics";
const COOKIE_ADVERTISING = "cookieConsent_advertising";
const COOKIE_EXPIRES_DAYS = 365;
const HEAD_TRACKING_SCRIPT_ID = "headTrackingScript";
const NOSCRIPT_TRACKING_SCRIPT_ID = "noscriptTrackingScript";
const GA4_TRACKING_SCRIPT_ID = "GA4Tracking";
const GOOGLE_TAG_MANAGER_PRECONNECT_ID = "googleTagManagerPreconnect";

const analyticsCookiePrefixes = ["_ga", "_gid", "_gat", "_gat_gtag", "_dc_gtm", "__utm", "_gac"];
const advertisingCookiePrefixes = ["_gcl_", "_gcl", "FPAU", "FPGCL", "IDE", "ANID", "test_cookie"];

const mapStateToProps = (state: AusState) => ({
    session: state.context.session,
    analyticsSettings: state.airmarAnalyticsSettings.value,
});

const mapDispatchToProps = {
    loadAirmarAnalyticsSettings,
};
export interface CookiePrivacyPolicyPopupStyles {
    drawer?: DrawerPresentationProps;
    container?: GridContainerProps;
    gridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    buttonsGridItem?: GridItemProps;
    acceptButton?: ButtonPresentationProps;
    rejectButton?: ButtonPresentationProps;
    manageButton?: ButtonPresentationProps;
    confirmButton?: ButtonPresentationProps;
    preferencesGridItem?: GridItemProps;
    preferencesHeading?: TypographyPresentationProps;
    categoryToggle?: CheckboxPresentationProps;
    categoryDescription?: TypographyPresentationProps;
    alwaysActiveGridItem?: GridItemProps;
    essentialCookiesHeader?: GridContainerProps;
    accordionSectionGridItem?: GridItemProps;
    accordionSection?: AccordionSectionPresentationProps;
}

export const cookiePrivacyPolicyPopupStyles: CookiePrivacyPolicyPopupStyles = {
    drawer: {
        position: "bottom",
        size: 100,
        isAutoSize: true,
        cssOverrides: {
            drawerTitle: css`
                display: none;
            `,
            drawerContent: css`
                padding: 20px 25px;
                margin: 0 auto;
                width: 100%;
                max-width: 1200px;
            `,
        },
        closeButtonProps: {
            css: css`
                display: none;
            `,
        },
        enableClickThrough: true,
    },
    container: {
        gap: 15,
    },
    gridItem: {
        width: 12,
    },
    buttonsGridItem: {
        width: 12,
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 15px;
            justify-content: space-between;
        `,
    },
    acceptButton: {},
    rejectButton: {
        variant: "secondary",
    },
    manageButton: {
        variant: "tertiary",
    },
    confirmButton: {},
    preferencesGridItem: {
        width: 12,
    },
    preferencesHeading: {
        weight: "bold",
        css: css`
            display: block;
            margin: 5px 0 10px;
            font-size: 16px;
        `,
    },
    categoryToggle: {
        css: css`
            pointer-events: none;
            [role="checkbox"] {
                pointer-events: auto;
                cursor: pointer;
            }
        `,
    },
    categoryDescription: {
        css: css`
            display: block;
            margin-top: 8px;
        `,
    },
    alwaysActiveGridItem: {
        css: css`
            justify-content: flex-end;
            text-align: right;
            font-weight: bold;
        `,
    },
    essentialCookiesHeader: {
        css: css`
            font-size: 15px;
            padding-left: 10px;
        `,
    },
    accordionSectionGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    accordionSection: {
        headerProps: {
            css: css`
                flex: 1 1 auto;
                width: 100%;
                button {
                    flex-direction: row-reverse;
                    justify-content: flex-end;
                }
            `,
        },
        panelProps: {
            css: css`
                width: 100%;
            `,
        },
        toggleIconProps: {
            src: Plus,
            size: 20,
        },
        collapseIconProps: {
            src: Minus,
            size: 20,
        },
        expandIconProps: {
            src: Plus,
            size: 20,
        },
    },
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = cookiePrivacyPolicyPopupStyles;

const readConsent = () => ({
    analytics: getCookie(COOKIE_ANALYTICS) !== undefined && getCookie(COOKIE_ANALYTICS) !== "false",
    advertising: getCookie(COOKIE_ADVERTISING) !== undefined && getCookie(COOKIE_ADVERTISING) !== "false",
});

const getDomainVariants = () => {
    if (typeof window === "undefined") {
        return [undefined];
    }

    const hostname = window.location.hostname;
    const segments = hostname.split(".").filter(Boolean);
    const domains = new Set<string | undefined>([undefined, hostname, `.${hostname}`]);

    if (segments.length > 2) {
        for (let i = 1; i < segments.length - 1; i++) {
            const domain = segments.slice(i).join(".");
            domains.add(domain);
            domains.add(`.${domain}`);
        }
    }

    return Array.from(domains);
};

const getPathVariants = () => {
    if (typeof window === "undefined") {
        return [undefined, "/"];
    }

    const pathname = window.location.pathname || "/";
    const paths = new Set<string | undefined>([undefined, "/"]);

    const parts = pathname.split("/").filter(Boolean);
    if (parts.length > 0) {
        let currentPath = "";
        parts.forEach(part => {
            currentPath += `/${part}`;
            paths.add(currentPath);
            paths.add(`${currentPath}/`);
        });
    }

    return Array.from(paths);
};

const removeCookieWithCommonOptions = (cookieName: string) => {
    const domains = getDomainVariants();
    const paths = getPathVariants();

    domains.forEach(domain => {
        paths.forEach(path => {
            if (domain && path) {
                removeCookie(cookieName, { domain, path });
                return;
            }

            if (domain) {
                removeCookie(cookieName, { domain });
                return;
            }

            if (path) {
                removeCookie(cookieName, { path });
                return;
            }

            removeCookie(cookieName);
        });
    });
};

const removeCookiesByPrefix = (prefixes: string[]) => {
    if (typeof document === "undefined") {
        return;
    }

    const cookieNames = document.cookie
        .split(";")
        .map(cookie => cookie.trim().split("=")[0])
        .filter(Boolean);

    cookieNames.forEach(cookieName => {
        if (prefixes.some(prefix => cookieName.startsWith(prefix))) {
            removeCookieWithCommonOptions(cookieName);
        }
    });
};

const applyConsent = (analytics: boolean, advertising: boolean) => {
    if (!analytics) {
        removeCookiesByPrefix(analyticsCookiePrefixes);
    }

    if (!advertising) {
        removeCookiesByPrefix(advertisingCookiePrefixes);
    }
};

const writeConsent = (analytics: boolean, advertising: boolean) => {
    setCookie(COOKIE_CONSENT, "true", { expires: COOKIE_EXPIRES_DAYS });
    setCookie(COOKIE_ANALYTICS, analytics ? "true" : "false", { expires: COOKIE_EXPIRES_DAYS });
    setCookie(COOKIE_ADVERTISING, advertising ? "true" : "false", { expires: COOKIE_EXPIRES_DAYS });
    applyConsent(analytics, advertising);
};

const removeTrackingElement = (elementId: string) => {
    if (typeof document === "undefined") {
        return;
    }

    document.getElementById(elementId)?.remove();
};

const CookiePrivacyPolicyPopup = ({ session, analyticsSettings, loadAirmarAnalyticsSettings }: Props) => {
    const location = useAppSelector(o => getLocation(o));
    const settings = useGetSetting(o => o);
    if (!settings) {
        return null;
    }

    const enableCookiePrivacyPolicyPopup = settings.websiteSettings.enableCookiePrivacyPolicyPopup;

    const [isBannerOpen, setIsBannerOpen] = useState(false);
    const [isPreferencesExpanded, setIsPreferencesExpanded] = useState(false);

    const [analyticsAllowed, setAnalyticsAllowed] = useState(false);
    const [advertisingAllowed, setAdvertisingAllowed] = useState(false);

    useEffect(() => {
        if (
            enableCookiePrivacyPolicyPopup &&
            !analyticsSettings?.googleTrackingTypeComputed &&
            !analyticsSettings?.googleTrackingAccountId
        ) {
            loadAirmarAnalyticsSettings({});
        }
    }, [
        analyticsSettings?.googleTrackingAccountId,
        analyticsSettings?.googleTrackingTypeComputed,
        enableCookiePrivacyPolicyPopup,
        loadAirmarAnalyticsSettings,
    ]);

    useEffect(() => {
        if (enableCookiePrivacyPolicyPopup && !getCookie(COOKIE_CONSENT)) {
            setIsBannerOpen(true);
        }
        const current = readConsent();
        setAnalyticsAllowed(current.analytics);
        setAdvertisingAllowed(current.advertising);

        if (getCookie(COOKIE_CONSENT)) {
            applyConsent(current.analytics, current.advertising);
        }
    }, [location.pathname]);

    const closeBanner = () => {
        setIsBannerOpen(false);
        setIsPreferencesExpanded(false);
    };

    const acceptAllHandler = () => {
        writeConsent(true, true);
        setAnalyticsAllowed(true);
        setAdvertisingAllowed(true);
        syncTrackingScripts();
        closeBanner();
    };

    const rejectNonEssentialHandler = () => {
        writeConsent(false, false);
        setAnalyticsAllowed(false);
        setAdvertisingAllowed(false);
        syncTrackingScripts();
        closeBanner();
    };

    const confirmPreferencesHandler = () => {
        writeConsent(analyticsAllowed, advertisingAllowed);
        syncTrackingScripts();
        closeBanner();
    };

    const togglePreferencesHandler = () => {
        setIsPreferencesExpanded(prev => !prev);
    };

    const essentialCookiesHeader = (
        <GridContainer {...styles.essentialCookiesHeader}>
            <GridItem width={6}>
                <Typography>{siteMessage("CookieBanner_EssentialCookiesHeader")}</Typography>
            </GridItem>
            <GridItem width={6} {...styles.alwaysActiveGridItem}>
                <Typography>{translate("Always Active")}</Typography>
            </GridItem>
        </GridContainer>
    );

    const analyticsPerformanceHeader = (
        <Checkbox
            {...styles.categoryToggle}
            variant="toggle"
            checked={analyticsAllowed}
            onChange={(e, value) => {
                e.stopPropagation();
                setAnalyticsAllowed(value);
            }}
            data-test-selector="cookiePreferences_AnalyticsToggle"
        >
            {siteMessage("CookieBanner_AnalyticsPerformanceHeader")}
        </Checkbox>
    );
    const targettedAdvertisingHeader = (
        <Checkbox
            {...styles.categoryToggle}
            variant="toggle"
            checked={advertisingAllowed}
            onChange={(e, value) => {
                e.stopPropagation();
                setAdvertisingAllowed(value);
            }}
            data-test-selector="cookiePreferences_AdvertisingToggle"
        >
            {siteMessage("CookieBanner_TargetedAdvertisingCookiesHeader")}
        </Checkbox>
    );

    const syncTrackingScripts = () => {
        if (typeof document === "undefined") {
            return;
        }

        const hasAnalyticsConsent =
            getCookie(COOKIE_CONSENT) === "true" && getCookie(COOKIE_ANALYTICS) === "true";

        if (!hasAnalyticsConsent) {
            removeTrackingElement(GOOGLE_TAG_MANAGER_PRECONNECT_ID);
            removeTrackingElement(GA4_TRACKING_SCRIPT_ID);
            removeTrackingElement(HEAD_TRACKING_SCRIPT_ID);
            removeTrackingElement(NOSCRIPT_TRACKING_SCRIPT_ID);
            return;
        }

        const googleTrackingTypeComputed =
            analyticsSettings?.googleTrackingTypeComputed;
        const googleTrackingAccountId =
            analyticsSettings?.googleTrackingAccountId;

        const noscriptTrackingScript = getNoscriptTrackingScript(analyticsSettings);
        const headTrackingScript = getHeadTrackingScript(analyticsSettings, session);
        const preconnectToGoogleTagManager =
            googleTrackingTypeComputed === "GoogleAnalytics4" || googleTrackingTypeComputed === "GoogleTagManager";

        let preconnect = document.getElementById(GOOGLE_TAG_MANAGER_PRECONNECT_ID) as HTMLLinkElement | null;
        if (preconnectToGoogleTagManager) {
            if (!preconnect) {
                preconnect = document.createElement("link");
                preconnect.setAttribute("id", GOOGLE_TAG_MANAGER_PRECONNECT_ID);
                preconnect.setAttribute("rel", "preconnect");
                preconnect.setAttribute("href", "https://www.googletagmanager.com");
                document.head.appendChild(preconnect);
            }
        } else {
            preconnect?.remove();
        }

        let ga4Script = document.getElementById(GA4_TRACKING_SCRIPT_ID) as HTMLScriptElement | null;
        if (googleTrackingTypeComputed === "GoogleAnalytics4" && googleTrackingAccountId) {
            if (!ga4Script) {
                ga4Script = document.createElement("script");
                ga4Script.setAttribute("id", GA4_TRACKING_SCRIPT_ID);
                ga4Script.setAttribute("async", "true");
                document.head.appendChild(ga4Script);
            }

            ga4Script.src = `https://www.googletagmanager.com/gtag/js?id=${googleTrackingAccountId}`;
        } else {
            ga4Script?.remove();
        }

        let headScript = document.getElementById(HEAD_TRACKING_SCRIPT_ID) as HTMLScriptElement | null;
        if (headTrackingScript) {
            if (!headScript) {
                headScript = document.createElement("script");
                headScript.setAttribute("id", HEAD_TRACKING_SCRIPT_ID);
                document.head.appendChild(headScript);
            }

            headScript.innerHTML = headTrackingScript;
        } else {
            headScript?.remove();
        }

        let noscript = document.getElementById(NOSCRIPT_TRACKING_SCRIPT_ID) as HTMLElement | null;
        if (noscriptTrackingScript) {
            if (!noscript) {
                noscript = document.createElement("noscript");
                noscript.setAttribute("id", NOSCRIPT_TRACKING_SCRIPT_ID);
                document.body.appendChild(noscript);
            }

            noscript.innerHTML = noscriptTrackingScript;
        } else {
            noscript?.remove();
        }
    };

    useEffect(() => {
        syncTrackingScripts();
    }, [
        analyticsAllowed,
        advertisingAllowed,
        analyticsSettings?.googleTrackingAccountId,
        analyticsSettings?.googleTrackingTypeComputed,
        session,
    ]);

    return (
        <Drawer isOpen={isBannerOpen} {...styles.drawer} data-test-selector="drawer_CookiePolicy">
            <GridContainer {...styles.container}>
                <GridItem {...styles.gridItem}>
                    <Typography {...styles.messageText} data-test-selector="cookiePolicy_PrivacyPolicy">
                        {siteMessage("Core_CookiePrivacyPolicy")}
                    </Typography>
                </GridItem>
                <GridItem {...styles.buttonsGridItem}>
                    <Button
                        {...styles.acceptButton}
                        onClick={acceptAllHandler}
                        data-test-selector="cookiePolicy_AcceptCookies"
                    >
                        {translate("Accept All")}
                    </Button>

                    {!isPreferencesExpanded && (
                        <><Button
                            {...styles.rejectButton}
                            onClick={rejectNonEssentialHandler}
                            data-test-selector="cookiePolicy_RejectNonEssential"
                            >
                                {translate("Reject Non-Essential")}
                            </Button>
                            <Button
                                {...styles.manageButton}
                                onClick={togglePreferencesHandler}
                                data-test-selector="cookiePolicy_ManagePreferences"
                            >
                                {translate("Manage Preferences")}
                            </Button>
                        </>
                    )}
                </GridItem>

                {isPreferencesExpanded && (
                    <GridItem {...styles.preferencesGridItem}>
                        <GridContainer gap={10}>
                            <GridItem {...styles.gridItem}>
                                <Typography {...styles.preferencesHeading}>
                                    {translate("Manage Cookie Preferences")}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.accordionSectionGridItem}>
                                <ManagedAccordionSection {...styles.accordionSection} title={essentialCookiesHeader}>
                                    <GridContainer gap={5}>
                                        <GridItem width={12}>
                                            <Typography {...styles.categoryDescription}>
                                                {siteMessage("CookieBanner_EssentialCookiesDescription")}
                                            </Typography>
                                        </GridItem>
                                    </GridContainer>
                                </ManagedAccordionSection>
                            </GridItem>
                            <GridItem {...styles.accordionSectionGridItem}>
                                <ManagedAccordionSection {...styles.accordionSection} title={analyticsPerformanceHeader}>
                                    <GridContainer gap={5}>
                                        <GridItem width={12}>
                                            <Typography {...styles.categoryDescription}>
                                                {siteMessage("CookieBanner_AnalyticsPerformanceDescription")}
                                            </Typography>
                                        </GridItem>
                                    </GridContainer>
                                </ManagedAccordionSection>
                            </GridItem>
                            <GridItem {...styles.accordionSectionGridItem}>
                                <ManagedAccordionSection {...styles.accordionSection} title={targettedAdvertisingHeader}>
                                    <GridContainer gap={5}>
                                        <GridItem width={12}>
                                            <Typography {...styles.categoryDescription}>
                                                {siteMessage("CookieBanner_TargetedAdvertisingCookiesDescription")}
                                            </Typography>
                                        </GridItem>
                                    </GridContainer>
                                </ManagedAccordionSection>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                )}

                <GridItem {...styles.buttonsGridItem}>

                    { isPreferencesExpanded && (
                        <>
                            <Button
                                {...styles.rejectButton}
                                onClick={rejectNonEssentialHandler}
                                data-test-selector="cookiePolicy_RejectNonEssential"
                            >
                                {translate("Reject Non-Essential")}
                            </Button>
                            <Button
                                {...styles.confirmButton}
                                onClick={confirmPreferencesHandler}
                                data-test-selector="cookiePolicy_ConfirmChoices"
                            >
                                {translate("Confirm My Choices")}
                            </Button>
                        </>
                    )}
                </GridItem>
            </GridContainer>
        </Drawer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CookiePrivacyPolicyPopup);